/**
 * 대한민국 시/도 및 시/군/구 데이터
 * 프론트엔드(지역 드롭다운)와 서버(지오코딩 결과 정규화) 양쪽에서 공용으로 사용
 */

export interface RegionData {
  [region: string]: {
    [province: string]: string[];
  };
}

export const REGIONS: RegionData = {
  "수도권": {
    "서울특별시": [
      "강남구", "강동구", "강북구", "강서구", "관악구", "광진구", "구로구", "금천구",
      "노원구", "도봉구", "동대문구", "동작구", "마포구", "서대문구", "서초구", "성동구",
      "성북구", "송파구", "양천구", "영등포구", "용산구", "은평구", "종로구", "중구", "중랑구"
    ],
    "경기도": [
      "수원시 장안구", "수원시 권선구", "수원시 팔달구", "수원시 영통구",
      "성남시 수정구", "성남시 중원구", "성남시 분당구",
      "의정부시",
      "안양시 만안구", "안양시 동안구",
      "부천시", "광명시", "평택시", "동두천시",
      "안산시 상록구", "안산시 단원구",
      "고양시 덕양구", "고양시 일산동구", "고양시 일산서구",
      "과천시", "구리시", "남양주시", "오산시", "시흥시", "군포시", "의왕시", "하남시",
      "용인시 처인구", "용인시 기흥구", "용인시 수지구",
      "파주시", "이천시", "안성시", "김포시", "화성시", "광주시", "양주시", "포천시",
      "여주시", "연천군", "가평군", "양평군"
    ],
    "인천광역시": [
      "계양구", "미추홀구", "남동구", "동구", "부평구", "서구", "연수구", "중구",
      "강화군", "옹진군"
    ]
  },
  "강원권": {
    "강원도": [
      "춘천시", "원주시", "강릉시", "동해시", "태백시", "속초시", "삼척시",
      "홍천군", "횡성군", "영월군", "평창군", "정선군", "철원군", "화천군",
      "양구군", "인제군", "고성군", "양양군"
    ]
  },
  "충청권": {
    "충청북도": [
      "청주시 상당구", "청주시 서원구", "청주시 흥덕구", "청주시 청원구",
      "충주시", "제천시", "보은군", "옥천군", "영동군", "증평군", "진천군",
      "괴산군", "음성군", "단양군"
    ],
    "충청남도": [
      "천안시 동남구", "천안시 서북구",
      "공주시", "보령시", "아산시", "서산시", "논산시", "계룡시", "당진시",
      "금산군", "부여군", "서천군", "청양군", "홍성군", "예산군", "태안군"
    ],
    "대전광역시": ["대덕구", "동구", "서구", "유성구", "중구"],
    "세종특별자치시": ["세종특별자치시"]
  },
  "전라권": {
    "전라북도": [
      "전주시 완산구", "전주시 덕진구",
      "군산시", "익산시", "정읍시", "남원시", "김제시",
      "완주군", "진안군", "무주군", "장수군", "임실군", "순창군", "고창군", "부안군"
    ],
    "전라남도": [
      "목포시", "여수시", "순천시", "나주시", "광양시",
      "담양군", "곡성군", "구례군", "고흥군", "보성군", "화순군", "장흥군",
      "강진군", "해남군", "영암군", "무안군", "함평군", "영광군", "장성군",
      "완도군", "진도군", "신안군"
    ],
    "광주광역시": ["광산구", "남구", "동구", "북구", "서구"]
  },
  "경상권": {
    "경상북도": [
      "포항시 남구", "포항시 북구",
      "경주시", "김천시", "안동시", "구미시", "영주시", "영천시", "상주시",
      "문경시", "경산시", "군위군", "의성군", "청송군", "영양군", "영덕군",
      "청도군", "고령군", "성주군", "칠곡군", "예천군", "봉화군", "울진군", "울릉군"
    ],
    "경상남도": [
      "창원시 의창구", "창원시 성산구", "창원시 마산합포구", "창원시 마산회원구", "창원시 진해구",
      "진주시", "통영시", "사천시", "김해시", "밀양시", "거제시", "양산시",
      "의령군", "함안군", "창녕군", "고성군", "남해군", "하동군", "산청군",
      "함양군", "거창군", "합천군"
    ],
    "부산광역시": [
      "강서구", "금정구", "남구", "동구", "동래구", "부산진구", "북구",
      "사상구", "사하구", "서구", "수영구", "연제구", "영도구", "중구",
      "해운대구", "기장군"
    ],
    "대구광역시": [
      "남구", "달서구", "동구", "북구", "서구", "수성구", "중구", "달성군"
    ],
    "울산광역시": ["남구", "동구", "북구", "중구", "울주군"]
  },
  "제주권": {
    "제주특별자치도": ["서귀포시", "제주시"]
  }
};

/**
 * 모든 시/도 목록 반환 (권역 구분 없이)
 */
export function getAllProvinces(): string[] {
  const provinces: string[] = [];
  Object.values(REGIONS).forEach(region => {
    provinces.push(...Object.keys(region));
  });
  return provinces;
}

/**
 * 특정 시/도의 시/군/구 목록 반환
 */
export function getDistricts(province: string): string[] {
  for (const region of Object.values(REGIONS)) {
    if (region[province]) {
      return region[province];
    }
  }
  return [];
}

// 행정구역 개편으로 이름이 바뀐 시/도. Google Geocoding 등 외부 데이터가 새 명칭(강원특별자치도/
// 전북특별자치도)을 반환해도 REGIONS의 구 명칭으로 맞춰 매칭할 수 있도록 정규화한다.
const PROVINCE_ALIASES: Record<string, string> = {
  '강원특별자치도': '강원도',
  '전북특별자치도': '전라북도',
  // 도로명주소 API가 돌려주는 축약 표기 (예: "경기 화성시 ...")
  '서울': '서울특별시',
  '부산': '부산광역시',
  '대구': '대구광역시',
  '인천': '인천광역시',
  '광주': '광주광역시',
  '대전': '대전광역시',
  '울산': '울산광역시',
  '세종': '세종특별자치시',
  '세종시': '세종특별자치시',
  '경기': '경기도',
  '강원': '강원도',
  '충북': '충청북도',
  '충남': '충청남도',
  '전북': '전라북도',
  '전남': '전라남도',
  '경북': '경상북도',
  '경남': '경상남도',
  '제주': '제주특별자치도',
  '제주도': '제주특별자치도',
};

const KNOWN_PROVINCES = new Set(getAllProvinces());

/**
 * 외부 데이터(지오코딩 등)에서 얻은 시/도 명칭을 REGIONS 기준으로 정규화.
 * REGIONS에 없는 값이면 null.
 */
export function normalizeProvince(name: string | null | undefined): string | null {
  if (!name) return null;
  const normalized = PROVINCE_ALIASES[name] || name;
  return KNOWN_PROVINCES.has(normalized) ? normalized : null;
}

/**
 * 외부 데이터에서 얻은 시/군/구 명칭을 REGIONS 기준으로 정규화.
 * province가 먼저 정규화되어 있어야 하며, 해당 province의 목록에 없는 값이면 null.
 */
export function normalizeDistrict(province: string | null | undefined, name: string | null | undefined): string | null {
  if (!province || !name) return null;
  return getDistricts(province).includes(name) ? name : null;
}

/**
 * 지오코딩 결과가 랜드마크/역 등 POI로 잡혀 administrative_area_level_1(시/도)이
 * 비어있을 때, locality(시/군/구 앞부분, 예: "용인시")만으로 시/도를 역추론.
 * 시/군/구 명칭은 전국에서 유일하므로(중복되는 "중구"/"동구" 등은 보통 sublocality라
 * locality 단독으로는 안 옴) 정확히 한 시/도에서만 매칭되면 그 시/도를 반환, 모호하면 null.
 */
export function inferProvinceFromLocality(locality: string | null | undefined): string | null {
  if (!locality) return null;
  const matches = new Set<string>();
  for (const provinces of Object.values(REGIONS)) {
    for (const [province, districts] of Object.entries(provinces)) {
      if (districts.some((d) => d === locality || d.startsWith(`${locality} `))) {
        matches.add(province);
      }
    }
  }
  return matches.size === 1 ? [...matches][0] : null;
}

/**
 * 지오코딩 결과에 sublocality_level_1(구)가 안 잡혔을 때, 원본 입력 주소 문자열에서
 * 해당 시/도의 시/군/구 명칭(예: "용인시 수지구")이 그대로 포함돼 있는지 찾아 역추론.
 * 여러 개가 매칭되면(예: "용인시"만 있고 "용인시 수지구"도 있는 경우) 가장 구체적인
 * (가장 긴) 것을 우선한다. 하나도 못 찾으면 null.
 */
export function inferDistrictFromAddress(
  province: string | null | undefined,
  address: string | null | undefined,
): string | null {
  if (!province || !address) return null;
  const matches = getDistricts(province).filter((d) => address.includes(d));
  if (matches.length === 0) return null;
  return matches.reduce((longest, d) => (d.length > longest.length ? d : longest));
}
